import React, { useContext, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, Gavel, Route } from 'lucide-react';
import { SlideDef } from './slides';
import { Editable } from './Editable';
import { SourceLine } from './SourceLine';
import { ImageSlot } from './ImageSlot';
import { MapBg, RideBar, StatusBar } from './Chrome';
import { KraftBg, Paperclip, Stamp, Tape } from './Dossier';
import { Examples, TagPicker } from './Parts';
import { StaticCtx, useCanEdit, useDeck } from './store';
import { breakEntry } from './Transitions';

export function Axis1Open({ d }: { d: SlideDef }) {
  return (
    <div className="absolute inset-0 bg-[#0b0b0d]">
      <div className="absolute inset-0 opacity-40"><MapBg dark route={false} /></div>
      <StatusBar dark />
      <div className="absolute left-[140px] top-[230px] flex w-[1000px] flex-col gap-8">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 22 }} className="flex h-[150px] w-[150px] items-center justify-center rounded-[38px] bg-blue-600 shadow-[0_20px_60px_-10px_rgba(37,99,235,0.6)]">
          <Route size={78} className="text-white" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }} className="font-app text-[22px] font-semibold uppercase tracking-[0.3em] text-blue-400">Axe I</motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.28 }}>
          <Editable k={`${d.id}:title`} ph="Titre de l'axe 1" max={90} size={68} boxClass="h-[200px]" className="font-app font-bold leading-[1.05] tracking-tight text-white" testId="axis1-title" metaDark />
        </motion.div>
        <div className="h-[6px] w-[420px] overflow-hidden rounded-full bg-white/10">
          <motion.div className="h-full bg-blue-600" initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.9, ease: 'easeInOut' }} />
        </div>
      </div>
      <div className="absolute bottom-[110px] right-[120px] h-[360px] w-[360px]"><ImageSlot sid={d.id} className="h-full w-full" dark frame="rounded-3xl" /></div>
    </div>
  );
}

export function AppPart({ d }: { d: SlideDef }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <MapBg />
      <StatusBar />
      <RideBar current={d.idx!} />
      <motion.div
        initial={{ y: 160, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 32 }}
        className="absolute bottom-0 left-16 flex h-[690px] w-[900px] flex-col gap-4 rounded-t-[36px] bg-white px-12 pb-10 pt-4 shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.35)]"
        data-testid={`app-sheet-${d.id}`}
      >
        <div className="mx-auto h-[6px] w-16 rounded-full bg-neutral-200" />
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-black px-4 py-1 font-app text-[20px] font-bold text-white">{d.part}</span>
          <span className="font-ui text-[15px] text-neutral-400">Axe I · étape {d.idx! + 1} sur 3</span>
          <div className="ml-auto"><TagPicker sid={d.id} /></div>
        </div>
        <Editable k={`${d.id}:title`} ph="Titre du sous-axe" max={90} size={42} boxClass="h-[112px]" className="font-app font-bold leading-[1.08] tracking-tight text-neutral-900" testId={`title-${d.id}`} />
        <div className="h-px bg-neutral-200" />
        <Editable k={`${d.id}:body`} ph="Contenu : idée principale, arguments, données…" max={700} size={25} boxClass="flex-1" className="font-ui leading-relaxed text-neutral-700" testId={`body-${d.id}`} />
        <SourceLine k={`${d.id}:body:src`} parentKey={`${d.id}:body`} testId={`body-src-${d.id}`} />
      </motion.div>
      <div className="absolute bottom-10 right-16 top-[160px] flex w-[540px] flex-col gap-3">
        <ImageSlot sid={d.id} className="h-[190px] shrink-0" frame="rounded-2xl bg-white p-2 shadow-lg" srcSize={11} />
        <Examples sid={d.id} variant="app" />
      </div>
    </div>
  );
}

export function Axis2Open({ d }: { d: SlideDef }) {
  const isStatic = useContext(StaticCtx);
  const [phase, setPhase] = useState(isStatic ? 1 : 0);
  const [wait] = useState(() => (breakEntry.v ? 2600 : 1100));
  useEffect(() => {
    if (isStatic) return;
    const t = setTimeout(() => setPhase(1), wait);
    return () => clearTimeout(t);
  }, [isStatic, wait]);
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#eef0f2]">
      <MapBg route={false} />
      <motion.div className="absolute inset-0" initial={false} animate={{ opacity: phase ? 1 : 0 }} transition={{ duration: 0.6 }}><KraftBg /></motion.div>
      {!phase && <StatusBar />}
      <AnimatePresence>
        {phase === 0 ? (
          <motion.div key="notif" layoutId="assign" initial={{ y: -140, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className="glass absolute left-[420px] top-[90px] flex w-[760px] items-center gap-5 rounded-[28px] border border-white/70 p-6 shadow-2xl" data-testid="axis2-notification">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-600 text-white"><Bell size={32} /></div>
            <div className="font-ui">
              <div className="text-[15px] font-semibold uppercase tracking-wider text-neutral-500">Notification · maintenant</div>
              <div className="font-app text-[30px] font-bold text-neutral-900">Assignation reçue</div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="folder" layoutId="assign" transition={{ duration: 0.6, ease: 'easeInOut' }}
            className="absolute left-[200px] top-[140px] h-[640px] w-[1200px] rotate-[-1deg]" data-testid="axis2-folder">
            <div className="absolute -top-[48px] left-10 h-[60px] w-[300px] rounded-t-xl bg-[#d9b98a] px-6 pt-3 font-elite text-[24px] tracking-widest text-[#4a3215]">DOSSIER</div>
            <div className="relative h-full w-full rounded-md bg-[#d9b98a] p-14 shadow-[0_30px_60px_-20px_rgba(40,20,0,0.7)]">
              <Paperclip className="absolute -top-12 right-[440px]" />
              <div className="flex items-center gap-4 font-type text-[20px] uppercase tracking-[0.25em] text-[#4a3215]"><Gavel size={24} /> Axe II</div>
              <div className="mt-8 w-[780px]">
                <Editable k={`${d.id}:title`} ph="Titre de l'axe 2" max={90} size={64} boxClass="h-[230px]" className="font-elite leading-[1.1] text-[#231507]" testId="axis2-title" />
              </div>
              <div className="absolute bottom-14 left-14 right-14 flex items-end justify-between">
                <div className="font-type text-[18px] text-[#4a3215]">Affaire « Plateformes numériques » · 3 pièces versées au dossier</div>
              </div>
              <Stamp className="absolute right-16 top-20 text-[40px]" rotate={-12} delay={0.5} testId="axis2-stamp">Assignation</Stamp>
              <div className="absolute bottom-24 right-16 h-[250px] w-[300px] rotate-[3deg]"><ImageSlot sid={d.id} className="h-full w-full" frame="bg-white p-2 shadow-xl" /></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Piece({ d }: { d: SlideDef }) {
  const num = d.idx! - 2;
  const { get } = useDeck();
  const canEdit = useCanEdit();
  const showImg = canEdit || !!get(`${d.id}:img`);
  return (
    <div className="absolute inset-0 overflow-hidden">
      <KraftBg />
      <motion.div
        initial={{ x: -60, opacity: 0, rotate: -3 }}
        animate={{ x: 0, opacity: 1, rotate: -0.8 }}
        transition={{ duration: 0.45, delay: 0.3, ease: 'easeOut' }}
        className="paper absolute left-[90px] top-[60px] flex h-[780px] w-[960px] flex-col gap-4 px-14 pb-10 pt-12"
        data-testid={`piece-sheet-${d.id}`}
      >
        <Paperclip className="absolute -left-3 -top-10 z-10" />
        <div className="flex items-center justify-between border-b-2 border-black/70 pb-3 font-type text-[16px] uppercase tracking-[0.2em] text-black/70">
          <span>Dossier « Plateformes numériques »</span><span>Axe II · {d.part}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-elite text-[22px] text-red-700">{d.part}</span>
          <TagPicker sid={d.id} />
        </div>
        <Editable k={`${d.id}:title`} ph="Titre du sous-axe" max={90} size={40} boxClass="h-[104px]" className="font-elite leading-[1.12] text-neutral-900" testId={`title-${d.id}`} />
        <Editable k={`${d.id}:body`} ph="Contenu de la pièce : faits, règle, analyse…" max={700} size={23} boxClass="flex-1" className="font-type leading-[1.6] text-neutral-800" testId={`body-${d.id}`} />
        <SourceLine k={`${d.id}:body:src`} parentKey={`${d.id}:body`} testId={`body-src-${d.id}`} label="Réf." />
        <Stamp className="absolute right-10 top-[112px] text-[32px]" rotate={-9} testId={`piece-stamp-${d.id}`}>Pièce n°{num}</Stamp>
      </motion.div>
      <div className="absolute bottom-10 right-[60px] top-[50px] flex w-[440px] flex-col gap-4">
        {showImg && (
          <motion.div initial={{ y: -30, opacity: 0, rotate: 5 }} animate={{ y: 0, opacity: 1, rotate: 2.5 }} transition={{ duration: 0.4, delay: 0.45 }} className="relative h-[220px] shrink-0">
            <Tape className="-top-3 left-[160px] z-10 rotate-[-4deg]" />
            <ImageSlot sid={d.id} className="h-full" frame="bg-white p-2 pb-1 shadow-xl" srcSize={11} />
          </motion.div>
        )}
        <Examples sid={d.id} variant="dossier" />
      </div>
    </div>
  );
}
